import { OEvent } from '../misc/OEvent.js';
import { Model } from './Model.js';
import { latestQuestionnaireResponsesVersion, latestQuestionnaireResponseVersion, QuestionnaireResponsesVersions } from '../recipes/QuestionnaireRecipes/QuestionnaireResponseRecipes.js';
import { calculateHashOfObj } from '@refinio/one.core/lib/util/object.js';
export const questionnaireResponsesType = latestQuestionnaireResponsesVersion;
export const questionnaireResponsesTypes = QuestionnaireResponsesVersions;
export const questionnaireResponseType = latestQuestionnaireResponseVersion;
/**
 * This model represents everything related to Questionnaires.
 *
 * At the moment this model is just managing questionnaire responses.
 * In the future this will most probably also manage questionnaires.
 */
export default class QuestionnaireModel extends Model {
    /**
     * Event is emitted when the incomplete questionnaire response data is updated.
     */
    onIncompleteResponse = new OEvent();
    /**
     * Event is emitted when the questionnaire response data is updated.
     */
    channelManager;
    leuteModel;
    participantsHash;
    incompleteParticipantsHash;
    availableQuestionnaires;
    disconnect;
    // @Override base class event
    onUpdated = new OEvent();
    /**
     * Construct a new instance
     *
     * @param channelManager - The channel manager instance
     * @param leuteModel - The leute model for identity access
     */
    constructor(channelManager, leuteModel) {
        super();
        this.channelManager = channelManager;
        this.leuteModel = leuteModel;
        this.availableQuestionnaires = [];
    }
    /**
     * Initialize this instance
     *
     * This must be done after the one instance was initialized.
     */
    async init() {
        this.state.assertCurrentState('Uninitialised');
        this.state.triggerEvent('init');
        const myId = await this.leuteModel.myMainIdentity();
        const mainChannel = await this.channelManager.createChannel([myId]);
        this.participantsHash = mainChannel.participantsHash;
        const incompleteChannel = await this.channelManager.createChannel([myId]);
        this.incompleteParticipantsHash = incompleteChannel.participantsHash;
        this.disconnect = this.channelManager.onUpdated(this.handleOnUpdated.bind(this));
    }
    /**
     * Shutdown module
     */
    async shutdown() {
        this.state.assertCurrentState('Initialised');
        if (this.disconnect) {
            this.disconnect();
        }
        this.state.triggerEvent('shutdown');
    }
    // #### Questionnaire functions ####
    /**
     * Get a list of available questionnaires
     */
    async questionnaires() {
        this.state.assertCurrentState('Initialised');
        return this.availableQuestionnaires;
    }
    /**
     * Get a specific questionnaire
     *
     * Note that this does not connect to the server behind the url. The url is
     * simply the id used by questionnaires. FHIR uses urls for identifying resources
     * such as questionnaires.
     *
     * @param url - The url of the questionnaire
     */
    async questionnaireByUrl(url) {
        this.state.assertCurrentState('Initialised');
        for (const questionnaire of this.availableQuestionnaires) {
            if (questionnaire.url === url) {
                return questionnaire;
            }
        }
        throw Error('Questionnaire with url ' + url + ' does not exist');
    }
    /**
     * Get a specific questionnaire
     *
     * @param name - The name of the questionnaire
     * @param language - Language of questionnaire. If empty, just return the first in any language.
     */
    async questionnaireByName(name, language) {
        this.state.assertCurrentState('Initialised');
        for (const questionnaire of this.availableQuestionnaires) {
            if (questionnaire.name === name && (!language || questionnaire.language === language)) {
                return questionnaire;
            }
        }
        throw Error(
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        'Questionnaire with name ' + name + ' and language ' + language + ' does not exist');
    }
    /**
     * Get a questionnaire url by name and language.
     *
     * @param name
     * @param language
     */
    async questionnaireUrlByName(name, language) {
        this.state.assertCurrentState('Initialised');
        for (const questionnaire of this.availableQuestionnaires) {
            if (questionnaire.name === name &&
                (!language || questionnaire.language === language) &&
                questionnaire.url) {
                return questionnaire.url;
            }
        }
        throw Error(
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        'Questionnaire with name ' + name + ' and language ' + language + ' does not exist');
    }
    /**
     * Checks whether a questionnaire exists.
     *
     * @param url - Url of the questionnaire
     */
    async hasQuestionnaireWithUrl(url) {
        this.state.assertCurrentState('Initialised');
        for (const questionnaire of this.availableQuestionnaires) {
            if (questionnaire.url === url) {
                return true;
            }
        }
        return false;
    }
    /**
     * Checks whether a questionnaire exists.
     *
     * @param name - Name of the questionnaire
     * @param language - Language of questionnaire. If empty, just check in any language.
     */
    async hasQuestionnaireWithName(name, language) {
        this.state.assertCurrentState('Initialised');
        for (const questionnaire of this.availableQuestionnaires) {
            if (questionnaire.name === name && (!language || questionnaire.language === language)) {
                return true;
            }
        }
        return false;
    }
    /**
     * Adding questionnaires to the available questionnaires list.
     *
     * Note: In the future questionnaires will be served by one as one objects.
     *       This function will then change or be removed.
     *
     * @param questionnaires - The list of the questionnaires that will be added
     */
    registerQuestionnaires(questionnaires) {
        this.state.assertCurrentState('Initialised');
        this.availableQuestionnaires.push(...questionnaires);
    }
    // #### Questionnaire response functions ####
    /**
     * Create a new response to a questionnaire
     *
     * @param response - The questionnaire response to post
     * @param name - The name for this collection. This could be something the user specifies in order to be identified easily.
     * @param type - An application specific type. It is up to the application what to do with it.
     * @param owner - Change the owner of the channel to post to. Defaults to the default channel person that is set in the channel manager.
     */
    async postResponse(response, name, type, owner) {
        this.state.assertCurrentState('Initialised');
        return await this.postResponseCollection([response], name, type, owner);
    }
    /**
     * Post multiple responses as a single collection.
     *
     * This means that later when querying the questionnaires, this collection will appear as single entry.
     * This is useful if you dynamically compose a big questionnaires from several partial questionnaires.
     *
     * @param responses - The list of questionnaire responses to post
     * @param name - The name for this collection. This could be something the user specifies in order to be identified easily.
     * @param type - An application specific type. It is up to the application what to do with it.
     * @param owner - Change the owner of the channel to post to. Defaults to the default channel person that is set in the channel manager.
     */
    async postResponseCollection(responses, name, type, owner) {
        this.state.assertCurrentState('Initialised');
        // We decided not to do any validation here, because it is done by the questionnaire builder.
        // If you post something wrong, then shame on you :-)
        // Post the result to the one instance
        await this.channelManager.postToChannel(this.participantsHash, {
            $type$: questionnaireResponsesType,
            name,
            type,
            response: responses
        }, owner);
        return calculateHashOfObj({
            $type$: questionnaireResponsesType,
            name,
            type,
            response: responses
        });
    }
    /**
     * Get a list of responses.
     */
    async responses() {
        this.state.assertCurrentState('Initialised');
        return this.channelManager.getObjects({
            // QuestionnaireResponses is intentionally cast into QuestionnaireResponses_2_0_0
            // QuestionnaireResponses_2_0_0 only has additions
            types: questionnaireResponsesTypes,
            participants: this.participantsHash
        });
    }
    /**
     * returns iterator for QuestionnaireResponses
     * @param queryOptions
     */
    async *responsesIterator(queryOptions) {
        this.state.assertCurrentState('Initialised');
        yield* this.channelManager.objectIterator({
            ...queryOptions,
            // QuestionnaireResponses is intentionally cast into QuestionnaireResponses_2_0_0
            // QuestionnaireResponses_2_0_0 only has additions
            types: questionnaireResponsesTypes,
            participants: this.participantsHash
        });
    }
    /**
     * Get a specific questionnaire response
     *
     * @param id - the id of the questionnaire response. It is the id field of the ObjectData.
     */
    async responsesById(id) {
        this.state.assertCurrentState('Initialised');
        const iterator = this.channelManager.objectIterator({
            // QuestionnaireResponses is intentionally cast into QuestionnaireResponses_2_0_0
            // QuestionnaireResponses_2_0_0 only has additions
            types: questionnaireResponsesTypes,
            participants: this.participantsHash,
            id
        });
        for await (const responses of iterator) {
            return responses;
        }
        throw new Error('The referenced object does not exist');
    }
    // ######### Incomplete Response Methods ########
    /**
     * Saving incomplete questionnaires.
     *
     * @param response - The incomplete response.
     * @param type - The type of the response. This is later used to find incomplete responses.
     * @param name - The name of the response
     */
    async postIncompleteResponse(response, type, name) {
        this.state.assertCurrentState('Initialised');
        await this.postIncompleteResponseCollection([response], type, name);
    }
    /**
     * Save incomplete questionnaire collection.
     *
     * @param responses - The response list. If this list is empty then it works exactly as markIncompleteResponseAsComplete.
     * @param type - The type of the response. This is later used to find incomplete responses.
     * @param name - The name of the response
     */
    async postIncompleteResponseCollection(responses, type, name) {
        this.state.assertCurrentState('Initialised');
        // Post the result to the one instance
        await this.channelManager.postToChannel(this.incompleteParticipantsHash, {
            $type$: questionnaireResponsesType,
            name,
            type,
            response: responses
        });
    }
    /**
     * Getting the latest incomplete questionnaire.
     *
     * @param type - type of incomplete response collection
     * @param since - not older than this date.
     * @returns the incomplete data, or null if there isn't such data.
     */
    async incompleteResponse(type, since) {
        this.state.assertCurrentState('Initialised');
        // Construct iterator
        const iterator = this.channelManager.objectIterator({
            // QuestionnaireResponses is intentionally cast into QuestionnaireResponses_2_0_0
            // QuestionnaireResponses_2_0_0 only has additions
            types: questionnaireResponsesTypes,
            participants: this.incompleteParticipantsHash,
            from: since
        });
        // Iterate over all entries and see if a type is present
        for await (const responses of iterator) {
            if (responses.data.type !== type) {
                continue;
            }
            // Check if an empty element is found => no incomplete entry
            if (responses.data.response.length === 0) {
                return null;
            }
            return responses;
        }
        return null;
    }
    /**
     * Check if incomplete questionnaires exists.
     *
     * @param type - The type of the incomplete response collection.
     * @param since - Not older than this date.
     * @returns
     */
    async hasIncompleteResponse(type, since) {
        this.state.assertCurrentState('Initialised');
        return (await this.incompleteResponse(type, since)) !== null;
    }
    /**
     * Marks an incomplete response as complete.
     *
     * Note: This simply posts an empty responses object to the incomplete channel.
     *
     * @param type - The type of the incomplete response collection.
     */
    async markIncompleteResponseAsComplete(type) {
        this.state.assertCurrentState('Initialised');
        await this.channelManager.postToChannel(this.incompleteParticipantsHash, {
            $type$: questionnaireResponsesType,
            type: type,
            response: []
        });
    }
    /**
     * Handler function for the 'updated' event
     * @param _channelInfoIdHash
     * @param channelParticipants
     * @param _channelOwner
     * @param timeOfEarliestChange
     * @param _data
     */
    async handleOnUpdated(_channelInfoIdHash, channelParticipants, _channelOwner, timeOfEarliestChange, _data) {
        this.state.assertCurrentState('Initialised');
        if (channelParticipants === this.participantsHash ||
            channelParticipants === this.incompleteParticipantsHash) {
            this.onUpdated.emit(timeOfEarliestChange);
            if (channelParticipants === this.incompleteParticipantsHash) {
                this.onIncompleteResponse.emit();
            }
        }
    }
}
//# sourceMappingURL=QuestionnaireModel.js.map